import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Search, BookOpen } from 'lucide-react';
import { Sidebar } from './Sidebar.js';
import { TopBar } from './TopBar.js';
import { TabBar } from './TabBar.js';
import { AccountMenu } from './AccountMenu.js';
import { RouteErrorBoundary } from './RouteErrorBoundary.js';
import { SyncStatusState } from '../ui/SyncStatus.js';
import { Dialog } from '../ui/Dialog.js';
import { getLocalTabs } from '../../db/operations.js';
import { useAuthStore } from '../../stores/authStore.js';
import { apiClient } from '../../utils/apiClient.js';

import { VerifyEmailBanner } from '../auth/VerifyEmailBanner.js';
import { StorageBanner } from './StorageBanner.js';
import { useToast } from '../ui/useToast.js';
import { ROUTINE_SYNC_REJECTED_EVENT } from '../../sync/routineSync.js';
import { TAB_EDIT_DROPPED_EVENT, TabEditDroppedDetail } from '../../sync/tabEditSync.js';
import { TAB_EDIT_COPY } from '../../utils/tabEdits.js';
import { fold } from '../../utils/textNormalize.js';

export interface AppShellProps {
  syncState?: SyncStatusState;
  queuedSyncCount?: number;
  onSyncClick?: () => void;
  pendingModerationCount?: number;
}

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const AppShell: React.FC<AppShellProps> = ({
  syncState = 'synced',
  queuedSyncCount = 0,
  onSyncClick,
  pendingModerationCount
}) => {
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [fetchedAdminCount, setFetchedAdminCount] = useState<number>(0);

  const location = useLocation();
  const toast = useToast();
  const notifiedRejections = useRef<Set<string>>(new Set());

  // FRE-170: the server refused a routine; tell the user once per id + updatedAt.
  useEffect(() => {
    const onRejected = (e: Event) => {
      const d = (e as CustomEvent<{ id: string; title: string; updatedAt?: string }>).detail;
      if (!d) return;
      const key = `${d.id}:${d.updatedAt ?? ''}`;
      if (notifiedRejections.current.has(key)) return;
      notifiedRejections.current.add(key);
      toast.error(`روتین «${d.title}» روی سرور ذخیره نشد. آن را ویرایش کنید یا دوباره تلاش کنید.`);
    };
    window.addEventListener(ROUTINE_SYNC_REJECTED_EVENT, onRejected);
    return () => window.removeEventListener(ROUTINE_SYNC_REJECTED_EVENT, onRejected);
  }, [toast]);

  // FRE-224: a queued tab edit was dropped/refused by the server.
  useEffect(() => {
    const onTabEditDropped = (e: Event) => {
      const d = (e as CustomEvent<TabEditDroppedDetail>).detail;
      if (!d) return;
      const key = `tab-edit-dropped:${d.tabId}:${d.reason}`;
      if (notifiedRejections.current.has(key)) return;
      notifiedRejections.current.add(key);
      window.setTimeout(() => notifiedRejections.current.delete(key), 3000);

      const title = d.title || 'تبلچر';
      if (d.reason === 'gone') {
        toast.error(TAB_EDIT_COPY.c09);
      } else {
        toast.error(TAB_EDIT_COPY.c08(title));
      }
    };
    window.addEventListener(TAB_EDIT_DROPPED_EVENT, onTabEditDropped);
    return () => window.removeEventListener(TAB_EDIT_DROPPED_EVENT, onTabEditDropped);
  }, [toast]);
  const { user } = useAuthStore();
  const isAdmin = user?.role === 'ADMIN';

  // Global Meta+K / Ctrl+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Capture PWA beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
  }, []);

  const handleInstallPwa = async () => {
    if (!deferredPrompt) return;
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch {
      // ignore
    } finally {
      setDeferredPrompt(null);
    }
  };

  // Fetch pending admin moderation count if ADMIN
  useEffect(() => {
    if (!isAdmin) return;
    let isMounted = true;
    apiClient
      .get<{ PENDING: number; edits?: { PENDING: number } }>('/api/admin/moderation/counts')
      .then((res) => {
        if (isMounted && typeof res?.PENDING === 'number') {
          const editsPending = res.edits?.PENDING ?? 0;
          setFetchedAdminCount(res.PENDING + editsPending);
        }
      })
      .catch(() => {});
    return () => {
      isMounted = false;
    };
  }, [isAdmin]);

  const effectiveAdminCount = pendingModerationCount ?? fetchedAdminCount;

  const getPageTitle = (pathname: string): string => {
    if (pathname.startsWith('/dashboard')) return 'خانه';
    if (pathname.startsWith('/tabs') || pathname.startsWith('/library')) return 'کتابخانه';
    if (pathname.startsWith('/routines')) return 'روتین‌ها';
    if (pathname.startsWith('/tuner')) return 'تیونر';
    if (pathname.startsWith('/marketplace')) return 'بازارچه';
    if (pathname.startsWith('/admin')) return 'مدیریت و بررسی';
    if (pathname.startsWith('/my-edits')) return 'پیشنهادهای من';
    return 'فرت‌بورد';
  };

  const title = getPageTitle(location.pathname);

  return (
    <div
      data-testid="app-shell"
      className="h-full bg-base-100 text-base-content flex flex-col lg:flex-row antialiased selection:bg-primary selection:text-white"
    >
      {/* FND-07: first Tab stop; jumps focus to the main landmark */}
      <a
        href="#content"
        data-testid="skip-to-content"
        onClick={(e) => {
          e.preventDefault();
          const main = document.getElementById('content');
          main?.focus();
          main?.scrollIntoView({ block: 'start' });
        }}
        className="sr-only focus:not-sr-only focus:fixed focus:top-3 focus:start-3 focus:z-[100] focus:px-4 focus:py-2.5 focus:rounded-xl focus:bg-primary-fill focus:text-white focus:text-sm focus:font-semibold focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-base-100"
      >
        پرش به محتوای اصلی
      </a>

      {/* Desktop Persistent Sidebar (>= 1024px) */}
      <Sidebar
        syncState={syncState}
        queuedSyncCount={queuedSyncCount}
        onSyncClick={onSyncClick}
        onSearchClick={() => setSearchOpen(true)}
        onAccountClick={() => setAccountMenuOpen(true)}
        pendingModerationCount={effectiveAdminCount}
      />

      {/* Mobile Sticky TopBar (< 1024px) */}
      <TopBar
        title={title}
        syncState={syncState}
        queuedSyncCount={queuedSyncCount}
        onSyncClick={onSyncClick}
        onSearchClick={() => setSearchOpen(true)}
        onAccountClick={() => setAccountMenuOpen(true)}
      />

      {/* Center Main Viewport */}
      <main id="content" tabIndex={-1} className="flex-1 min-w-0 min-h-0 overflow-y-auto focus:outline-none">
        <div className="w-full px-4 pt-5 pb-28 md:px-8 md:py-6 md:pb-28 lg:px-10 lg:pt-8 lg:pb-14">
          <div className="mx-auto w-full max-w-[1120px]">
            <StorageBanner />
            <VerifyEmailBanner />
            <RouteErrorBoundary resetKey={location.pathname}>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </div>
      </main>

      {/* Mobile Fixed Bottom TabBar (< 1024px) */}
      <TabBar />

      {/* Account Menu Sheet/Modal */}
      <AccountMenu
        open={accountMenuOpen}
        onClose={() => setAccountMenuOpen(false)}
        canInstallPwa={Boolean(deferredPrompt)}
        onInstallPwa={handleInstallPwa}
      />

      {/* Global Quick Search Dialog */}
      <QuickSearchDialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
      />
    </div>
  );
};

AppShell.displayName = 'AppShell';

interface QuickSearchDialogProps {
  open: boolean;
  onClose: () => void;
}

const QuickSearchDialog: React.FC<QuickSearchDialogProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [tabs, setTabs] = useState<Array<{ id: string; title: string; artist: string }>>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setSelectedIndex(0);
      return;
    }
    getLocalTabs()
      .then((items) => {
        setTabs(items.map((t) => ({ id: t.id, title: t.title, artist: t.artist })));
      })
      .catch(() => {});
  }, [open]);

  const filteredTabs = useMemo(() => {
    const q = fold(query.trim());
    if (!q) return tabs.slice(0, 6);
    return tabs
      .filter(
        (t) =>
          fold(t.title).includes(q) ||
          fold(t.artist).includes(q)
      )
      .slice(0, 8);
  }, [query, tabs]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredTabs]);

  const handleSelect = (id: string) => {
    onClose();
    navigate(`/tabs/${id}`);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        filteredTabs.length > 0 ? (prev + 1) % filteredTabs.length : 0
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        filteredTabs.length > 0
          ? (prev - 1 + filteredTabs.length) % filteredTabs.length
          : 0
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredTabs[selectedIndex]) {
        handleSelect(filteredTabs[selectedIndex].id);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} title="جستجوی سریع" size="md">
      <div className="flex flex-col gap-4">
        <div className="relative">
          <Search className="size-4 absolute start-3 top-3 text-base-content/50" />
          <input
            autoFocus
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="جستجو در قطعه‌ها و تبلچرها…"
            className="w-full h-10 ps-9 pe-3 rounded-xl bg-base-100 border border-base-300 text-sm text-base-content placeholder:text-base-content/40 focus:outline-none focus:border-primary"
          />
        </div>

        {filteredTabs.length > 0 ? (
          <div className="flex flex-col gap-1 max-h-64 overflow-y-auto">
            {filteredTabs.map((item, idx) => (
              <button
                key={item.id}
                type="button"
                onClick={() => handleSelect(item.id)}
                className={`flex items-center justify-between p-2.5 rounded-xl text-start transition cursor-pointer ${
                  idx === selectedIndex
                    ? 'bg-primary/15 text-primary-soft'
                    : 'hover:bg-base-300/60 text-base-content'
                }`}
              >
                <div className="flex flex-col min-w-0">
                  <span className="text-sm font-semibold truncate" dir="ltr">
                    {item.title}
                  </span>
                  <span className="text-xs text-base-content/60 truncate" dir="ltr">
                    {item.artist}
                  </span>
                </div>
                <BookOpen className="size-4 shrink-0 opacity-60" />
              </button>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-sm text-base-content/50">
            {query ? 'موردی یافت نشد.' : 'تبلچری در دستگاه موجود نیست.'}
          </div>
        )}
      </div>
    </Dialog>
  );
};
